import { SoilTestListItem } from '@/api/growth/dashboard/type';

interface GrowthDashboardSoilTextureTriangleCardProps {
    currentSoil: SoilTestListItem;
    optimalOne: string;
    optimalTwo: string | null;
}

const GrowthDashboardSoilTextureTriangleCard = ({
    currentSoil,
    optimalOne,
    optimalTwo,
}: GrowthDashboardSoilTextureTriangleCardProps) => {
    // 토성 한글 변환
    const getSoilTextureKorean = (texture: string) => {
        const textureMap: { [key: string]: string } = {
            sand: '사토',
            'loamy sand': '양질 사토',
            'sandy loam': '사양토',
            loam: '양토',
            'silt loam': '미사질 양토',
            silt: '미사토',
            'sandy clay loam': '사질 식양토',
            'clay loam': '식양토',
            'silty clay loam': '미사질 식양토',
            'sandy clay': '사질 식토',
            'silty clay': '미사질 식토',
            clay: '식토',
        };
        return textureMap[texture.toLowerCase()] || texture;
    };

    const currentSoilKorean = getSoilTextureKorean(currentSoil.soilTexture);

    // 색상 결정 로직 (ASIS 코드 참고)
    const fillColor = (soilType: string) => {
        // 1. 현재 토양이 최적 토양과 일치 → 파란색
        if (
            (currentSoilKorean === optimalOne &&
                currentSoilKorean === soilType) ||
            (optimalTwo &&
                currentSoilKorean === optimalTwo &&
                currentSoilKorean === soilType)
        ) {
            return '#0072F5';
        }

        // 2. 현재 토양 (최적이 아님) → 노란색
        if (currentSoilKorean === soilType) {
            return '#C28C00';
        }

        // 3. 최적 토양 영역 → 연한 녹색
        if (
            soilType === optimalOne ||
            (optimalTwo && soilType === optimalTwo)
        ) {
            return '#E7F4E6';
        }

        // 4. 그 외 → 투명
        return 'transparent';
    };

    const strokeAndTextColor = (soilType: string) => {
        // 1. 현재 토양이 최적 토양과 일치 → 파란색
        if (
            (currentSoilKorean === optimalOne &&
                currentSoilKorean === soilType) ||
            (optimalTwo &&
                currentSoilKorean === optimalTwo &&
                currentSoilKorean === soilType)
        ) {
            return '#0072F5';
        }

        // 2. 현재 토양 (최적이 아님) → 노란색
        if (currentSoilKorean === soilType) {
            return '#C28C00';
        }

        // 3. 최적 토양 영역 → 녹색
        if (
            soilType === optimalOne ||
            (optimalTwo && soilType === optimalTwo)
        ) {
            return '#050';
        }

        // 4. 그 외 → 회색
        return '#333';
    };

    const config = {
        strokeWidth: 1,
        showLabels: true,
    };

    // 삼각형 좌표 계산
    const triangleBase = 500;
    const triangleHeight = triangleBase * Math.sin(Math.PI / 3);
    const centerX = 400;
    const topY = 80;
    const bottomY = topY + triangleHeight;

    const apex = { x: centerX, y: topY };
    const leftCorner = { x: centerX - triangleBase / 2, y: bottomY };
    const rightCorner = { x: centerX + triangleBase / 2, y: bottomY };

    // 격자선 생성
    const generateGridLines = () => {
        const lines = [];
        const steps = 10;

        for (let i = 1; i < steps; i++) {
            const ratio = i / steps;

            // 왼쪽에서 오른쪽으로 (Sand 축)
            const leftToRight = {
                x1: leftCorner.x + (rightCorner.x - leftCorner.x) * ratio,
                y1: bottomY,
                x2: apex.x + (rightCorner.x - apex.x) * ratio,
                y2: apex.y + (bottomY - apex.y) * ratio,
            };
            lines.push(
                <line
                    key={`lr-${i}`}
                    {...leftToRight}
                    stroke="#666"
                    strokeWidth="0.5"
                />,
            );

            // 오른쪽에서 왼쪽으로 (Silt 축)
            const rightToLeft = {
                x1: rightCorner.x - (rightCorner.x - leftCorner.x) * ratio,
                y1: bottomY,
                x2: apex.x - (apex.x - leftCorner.x) * ratio,
                y2: apex.y + (bottomY - apex.y) * ratio,
            };
            lines.push(
                <line
                    key={`rl-${i}`}
                    {...rightToLeft}
                    stroke="#aaa"
                    strokeWidth="0.5"
                />,
            );

            // 위에서 아래로 (Clay 축)
            const topToBottom = {
                x1: leftCorner.x + (apex.x - leftCorner.x) * ratio,
                y1: leftCorner.y - (leftCorner.y - apex.y) * ratio,
                x2: rightCorner.x - (rightCorner.x - apex.x) * ratio,
                y2: rightCorner.y - (rightCorner.y - apex.y) * ratio,
            };
            lines.push(
                <line
                    key={`tb-${i}`}
                    {...topToBottom}
                    stroke="#aaa"
                    strokeWidth="0.5"
                />,
            );
        }
        return lines;
    };

    // 축 라벨 생성
    const generateAxisLabels = () => {
        const labels = [];
        const steps = 10;

        for (let i = 0; i <= steps; i++) {
            const value = i * 10;
            const ratio = i / steps;

            // Sand 축 (하단)
            const sandX = leftCorner.x + (rightCorner.x - leftCorner.x) * ratio;
            labels.push(
                <text
                    key={`sand-${i}`}
                    x={sandX}
                    y={bottomY + 25}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#333"
                >
                    {100 - value}
                </text>,
            );

            // Silt 축 (우측)
            const siltY = bottomY - (bottomY - apex.y) * ratio;
            labels.push(
                <text
                    key={`silt-${i}`}
                    x={rightCorner.x + 30 - i * 25}
                    y={siltY + 4}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#333"
                >
                    {100 - value}
                </text>,
            );

            // Clay 축 (좌측)
            const clayY = bottomY - (bottomY - apex.y) * ratio;
            labels.push(
                <text
                    key={`clay-${i}`}
                    x={leftCorner.x - 30 + i * 25}
                    y={clayY + 4}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#333"
                >
                    {value}
                </text>,
            );
        }
        return labels;
    };

    return (
        <div className="card custom-card p-2 weather-card h-100">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center w-100 flex-wrap">
                    <h4 className="mb-1">토양상태</h4>
                    <div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '20px',
                                    marginTop: '8px',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            background: '#0072F5',
                                            border: '1px solid #999',
                                        }}
                                    ></div>
                                    <span>현재 최적</span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            background: '#C28C00',
                                            border: '1px solid #999',
                                        }}
                                    ></div>
                                    <span>현재 토양</span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            background: '#E7F4E6',
                                            border: '1px solid #999',
                                        }}
                                    ></div>
                                    <span>최적 토양</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card-body weather-info">
                {/* 현재 토양 상태 표시 */}
                <div className="mb-3 p-2 bg-light rounded">
                    <div className="row align-items-center text-center m-2">
                        <div className="col-12 col-md-3">
                            <h6 className="mb-1">현재 토양상태</h6>
                            <p
                                className={`mb-0 font-weight-bold ${
                                    currentSoilKorean === optimalOne ||
                                    currentSoilKorean === optimalTwo
                                        ? 'text-primary'
                                        : 'text-warning'
                                }`}
                            >
                                {currentSoilKorean}
                            </p>
                        </div>
                        <div className="col-12 col-md-3">
                            <h6 className="mb-1">점토</h6>
                            <p className="mb-0">
                                {currentSoil.clay.toFixed(1)}%
                            </p>
                        </div>
                        <div className="col-12 col-md-3">
                            <h6 className="mb-1">모래</h6>
                            <p className="mb-0">
                                {currentSoil.sand.toFixed(1)}%
                            </p>
                        </div>
                        <div className="col-12 col-md-3">
                            <h6 className="mb-1">미사</h6>
                            <p className="mb-0">
                                {currentSoil.silt.toFixed(1)}%
                            </p>
                        </div>
                    </div>
                </div>

                {/* 삼각형 다이어그램 */}
                <div className="d-flex justify-content-center align-items-center">
                    <svg
                        viewBox="0 30 800 575"
                        style={{
                            maxWidth: '800px',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        {/* 배경 삼각형 */}
                        <polygon
                            points={`${apex.x},${apex.y} ${leftCorner.x},${leftCorner.y} ${rightCorner.x},${rightCorner.y}`}
                            fill="#f5f5f5"
                            stroke="#333"
                            strokeWidth="0"
                        />

                        {/* 격자선 */}
                        {generateGridLines()}

                        {/* 토양 섹터 */}
                        <g transform="translate(144, 79) scale(1.58) rotate(-0.6)">
                            {/* 사토 */}
                            <path
                                d="M43.91 277.376H.85l15.038-27.075 28.022 27.075z"
                                fill={fillColor('사토')}
                                stroke={strokeAndTextColor('사토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 미사토 */}
                            <path
                                d="M296.241 242.891l20.872 34.484h-73.399l20.662-34.484h31.865z"
                                fill={fillColor('미사토')}
                                stroke={strokeAndTextColor('미사토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 미사질 양토 */}
                            <path
                                d="M272.153 196.256l21.315 40.06h-30.926l-.146.244-24.293 40.816h-81.708l55.227-81.12h60.531z"
                                fill={fillColor('미사질 양토')}
                                stroke={strokeAndTextColor('미사질 양토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 미사질 식양토 */}
                            <path
                                d="M198.608 165.83h56.759l14.241 25.877h-59.122l-11.878-25.877z"
                                fill={fillColor('미사질 식양토')}
                                stroke={strokeAndTextColor('미사질 식양토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 양질 사토 */}
                            <path
                                d="M49.395 277.372l-31.51-30.493 9.86-15.458 71.577 44.974-49.927.977z"
                                fill={fillColor('양질 사토')}
                                stroke={strokeAndTextColor('양질 사토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 사양토 */}
                            <path
                                d="M139.009 258.363l.145.244h22.683l-10.363 18.763-45.39-1.004-76.338-48.332 5.524-9.473h79.958l23.781 39.802z"
                                fill={fillColor('사양토')}
                                stroke={strokeAndTextColor('사양토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 식양토 */}
                            <path
                                d="M129.164 191.707l-14.484-24.864 78.234-1.009 12.916 25.873h-76.666z"
                                fill={fillColor('식양토')}
                                stroke={strokeAndTextColor('식양토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 양토 */}
                            <path
                                d="M142.525 254.889l-24.17-39.346 13.459-19.287h73.857l-41.527 58.633h-21.619z"
                                fill={fillColor('양토')}
                                stroke={strokeAndTextColor('양토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 식토 */}
                            <path
                                d="M114.109 162.265l-22.382-40.007L162.674 1.006l59.46 106.043-29.984 55.216h-78.041z"
                                fill={fillColor('식토')}
                                stroke={strokeAndTextColor('식토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 사질 식양토 */}
                            <path
                                d="M56.952 180.663h60.514l8.558 15.079-11.528 18.27H37.334l19.618-33.349z"
                                fill={fillColor('사질 식양토')}
                                stroke={strokeAndTextColor('사질 식양토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 사질 식토 */}
                            <path
                                d="M59.673 176.498l28.998-50.675 26.78 50.675H59.673z"
                                fill={fillColor('사질 식토')}
                                stroke={strokeAndTextColor('사질 식토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />
                            {/* 미사질 식토 */}
                            <path
                                d="M197.454 161.29l27.912-48.769 27.912 48.769h-55.824z"
                                fill={fillColor('미사질 식토')}
                                stroke={strokeAndTextColor('미사질 식토')}
                                fillOpacity={0.3}
                                strokeWidth={config.strokeWidth}
                            />

                            {/* 텍스트 라벨 */}
                            {config.showLabels && (
                                <>
                                    <text
                                        x="162"
                                        y="50"
                                        fill={strokeAndTextColor('식토')}
                                        fontSize="11"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        식토
                                    </text>
                                    <text
                                        x="88"
                                        y="168"
                                        fill={strokeAndTextColor('사질 식토')}
                                        fontSize="9"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        사질 식토
                                    </text>
                                    <text
                                        x="225"
                                        y="155"
                                        fill={strokeAndTextColor('미사질 식토')}
                                        fontSize="8"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        미사질 식토
                                    </text>
                                    <text
                                        x="160"
                                        y="183"
                                        fill={strokeAndTextColor('식양토')}
                                        fontSize="11"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        식양토
                                    </text>
                                    <text
                                        x="80"
                                        y="200"
                                        fill={strokeAndTextColor('사질 식양토')}
                                        fontSize="9"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        사질 식양토
                                    </text>
                                    <text
                                        x="232"
                                        y="182"
                                        fill={strokeAndTextColor(
                                            '미사질 식양토',
                                        )}
                                        fontSize="8"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        미사질 식양토
                                    </text>
                                    <text
                                        x="153"
                                        y="225"
                                        fill={strokeAndTextColor('양토')}
                                        fontSize="11"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        양토
                                    </text>
                                    <text
                                        x="120"
                                        y="270"
                                        fill={strokeAndTextColor('사양토')}
                                        fontSize="9"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        사양토
                                    </text>
                                    <text
                                        x="208"
                                        y="270"
                                        fill={strokeAndTextColor('미사질 양토')}
                                        fontSize="9"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        미사질 양토
                                    </text>
                                    <text
                                        x="61"
                                        y="270"
                                        fill={strokeAndTextColor('양질 사토')}
                                        fontSize="8"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        양질 사토
                                    </text>
                                    <text
                                        x="18"
                                        y="270"
                                        fill={strokeAndTextColor('사토')}
                                        fontSize="9"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        사토
                                    </text>
                                    <text
                                        x="278"
                                        y="270"
                                        fill={strokeAndTextColor('미사토')}
                                        fontSize="9"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        미사토
                                    </text>
                                </>
                            )}
                        </g>

                        {/* 축 라벨 */}
                        {generateAxisLabels()}

                        {/* 축 제목 */}
                        <text
                            x={centerX}
                            y={bottomY + 60}
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="bold"
                            fill="#333"
                        >
                            모래 함량(%)
                        </text>
                        <text
                            x={rightCorner.x - 60}
                            y={centerX + 80}
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="bold"
                            fill="#333"
                            transform={`rotate(65, ${rightCorner.x + 80}, ${centerX})`}
                        >
                            미사 함량(%)
                        </text>
                        <text
                            x={leftCorner.x + 65}
                            y={centerX + 65}
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="bold"
                            fill="#333"
                            transform={`rotate(-60, ${leftCorner.x - 80}, ${centerX})`}
                        >
                            점토 함량(%)
                        </text>
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default GrowthDashboardSoilTextureTriangleCard;
